import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { DateField } from "@/components/ui/date-field";
import { Badge } from "@/components/ui/badge";
import { FolderPlus, Upload, Trash2, FileText, ChevronRight, ChevronLeft, Sparkles, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtEur, fmtDate } from "@/lib/format";
import useHiddenModules from "@/lib/useHiddenModules";
import { DossierItemPicker, DossierTextEditor } from "@/components/DossierItems";

const TL_KINDS = [
  { value: "call", label: "Gesprekken/notities" },
  { value: "letter", label: "Brieven" },
  { value: "payment", label: "Betalingen" },
  { value: "payout", label: "Uitbetalingen" },
  { value: "complaint", label: "Klachten" },
  { value: "document", label: "Geüploade documenten" },
];
const DOC_SRC = { upload: "Document", brief: "Brief", contract: "Contract", overeenkomst: "Overeenkomst" };
const PSTAT = { paid_ontime: "Betaald", paid_late: "Betaald (te laat)", partial: "Deels betaald", open: "Openstaand", late: "Te laat", paid: "Betaald" };

export default function DossierWizard({ open, onOpenChange, tenantId, contracts = [], onSaved }) {
  const [step, setStep] = useState(0);
  const hidden = useHiddenModules();
  const noPay = hidden.includes("tab-betalingen");
  const noFact = hidden.includes("tab-factoring");
  const kinds = TL_KINDS.filter((k) => (k.value !== "payment" || !noPay) && (k.value !== "payout" || !hidden.includes("payouts")) && (k.value !== "complaint" || !hidden.includes("complaints")));
  const [tlInclude, setTlInclude] = useState(true);
  const stepKeys = ["gegevens", ...(noPay ? [] : ["betalingen"]), "tijdslijn", ...(tlInclude ? ["selectie"] : []), "documenten", "brief", "tekst", "overzicht"];
  const cur = stepKeys[step];
  const [texts, setTexts] = useState({});
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // betalingen
  const [payInclude, setPayInclude] = useState(true);
  const [sel, setSel] = useState("all");
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);
  const [onlyLate, setOnlyLate] = useState(false);
  const [rows, setRows] = useState([]);
  const [checkedIds, setCheckedIds] = useState({});
  const [loadingRows, setLoadingRows] = useState(false);
  // tijdslijn
  const [tlKinds, setTlKinds] = useState(Object.fromEntries(TL_KINDS.map((k) => [k.value, true])));
  const [tlScope, setTlScope] = useState("all");
  const [tlContract, setTlContract] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tlEvents, setTlEvents] = useState([]);
  const [tlChecked, setTlChecked] = useState({});
  // brief + bijlagen
  const [clInclude, setClInclude] = useState(false);
  const [clSubject, setClSubject] = useState("");
  const [clBody, setClBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docChecked, setDocChecked] = useState({});
  const docKey = (d) => `${d.source}:${d.id}`;
  const docsSelected = docs.filter((d) => docChecked[docKey(d)]);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const aiCover = async () => {
    setAiBusy(true);
    try {
      const parts = [!noPay && payInclude ? "betalingsoverzicht" : "", tlInclude ? "communicatie-tijdslijn" : "", attachments.length ? `${attachments.length} bijlage(n)` : ""].filter(Boolean).join(", ");
      const r = await api.post("/dossiers/ai-cover-letter", { tenant_id: tenantId, title, description, parts });
      setClSubject(r.data.subject || ""); setClBody(r.data.body || ""); setClInclude(true);
      toast.success("AI-suggestie ingevuld — pas gerust aan");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI-suggestie mislukt"); }
    setAiBusy(false);
  };
  const EditLink = ({ to }) => (
    <button type="button" onClick={() => setStep(stepKeys.indexOf(to))} className="ml-2 inline-flex items-center gap-1 text-xs text-[#0F1E3D] hover:text-[#C9A227] hover:underline" data-testid={`dossier-edit-${to}`}><Pencil className="h-3 w-3" /> Wijzigen</button>
  );

  useEffect(() => {
    if (open) {
      setStep(0); setTitle(""); setDescription("");
      setPayInclude(true); setSel("all"); setOnlyOutstanding(false); setOnlyLate(false); setCheckedIds({});
      setTlInclude(true); setTlKinds(Object.fromEntries(TL_KINDS.map((k) => [k.value, true]))); setTlScope("all"); setTlContract(""); setFrom(""); setTo("");
      setTlEvents([]); setTlChecked({}); setTexts({});
      api.get(`/tenants/${tenantId}/timeline`).then((r) => { setTlEvents(r.data || []); setTlChecked(Object.fromEntries((r.data || []).map((e) => [e.id, true]))); }).catch(() => {});
      setClInclude(false); setClSubject(""); setClBody(""); setAttachments([]);
      api.get("/documents", { params: { tenant_id: tenantId } }).then((r) => { setDocs(r.data || []); setDocChecked(Object.fromEntries((r.data || []).map((d) => [`${d.source}:${d.id}`, true]))); }).catch(() => setDocs([]));
    }
  }, [open]);

  useEffect(() => {
    if (!open || !payInclude) return;
    setLoadingRows(true);
    api.get(`/tenants/${tenantId}/statement`, { params: { sel } })
      .then((r) => {
        let rws = r.data.rows || [];
        setRows(rws);
        setCheckedIds(Object.fromEntries(rws.map((x) => [x.id, true])));
      })
      .catch(() => setRows([]))
      .finally(() => setLoadingRows(false));
  }, [open, sel, payInclude, tenantId]);

  const visibleRows = onlyLate
    ? rows.filter((r) => r.status === "late")
    : onlyOutstanding
      ? rows.filter((r) => ["open", "late", "partial"].includes(r.status))
      : rows;
  const inScope = (e) => {
    if (tlScope === "contract" && tlContract) return e.kind === "letter" || (["payment", "call"].includes(e.kind) && e.meta?.contract_id === tlContract);
    if (tlScope === "late") return ["letter", "call"].includes(e.kind) || (e.kind === "payment" && ["late", "paid_late"].includes(e.type));
    return true;
  };
  const tlItems = tlEvents.filter((e) => kinds.some((k) => k.value === e.kind) && tlKinds[e.kind] && inScope(e) && (!from || (e.date || "") >= from) && (!to || (e.date || "").slice(0, 10) <= to));
  const tlSelected = tlItems.filter((e) => tlChecked[e.id]);
  const toggleRow = (id) => setCheckedIds((s) => ({ ...s, [id]: !s[id] }));

  const uploadFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, { name: file.name, file_url: r.data.url }]);
      } catch { toast.error(`Upload mislukt: ${file.name}`); }
    }
    setUploading(false);
  };

  const create = async () => {
    const payOn = payInclude && !noPay;
    if (!title.trim()) { toast.error("Geef het dossier een titel"); return; }
    const chosen = visibleRows.filter((r) => checkedIds[r.id]).map((r) => r.id);
    const payload = {
      title: title.trim(), description,
      payment: { include: payOn, sel, only_outstanding: onlyOutstanding, only_late: onlyLate,
        payment_ids: payOn ? chosen : null },
      timeline: { include: tlInclude, kinds: kinds.map((k) => k.value).filter((k) => tlKinds[k]), date_from: from, date_to: to, scope: tlScope, contract_id: tlScope === "contract" ? tlContract : "",
        item_ids: tlInclude ? tlSelected.map((e) => e.id) : null },
      cover_letter: { include: clInclude, subject: clSubject, body: clBody },
      attachments,
      documents: docsSelected.map((d) => ({ source: d.source, id: d.id, title: d.title })),
      item_texts: Object.fromEntries(Object.entries(texts).filter(([k]) => tlSelected.some((e) => e.id === k))),
    };
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/dossiers`, payload);
      toast.success(`Dossier ${r.data.number} aangemaakt`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dossier aanmaken mislukt"); }
    setBusy(false);
  };

  const STEP_LABELS = { gegevens: "Gegevens", betalingen: "Betalingen", tijdslijn: "Tijdslijn", selectie: "Selectie", documenten: "Documenten", brief: "Brief & bijlagen", tekst: "Tekst bewerken", overzicht: "Overzicht" };
  const steps = stepKeys.map((k) => STEP_LABELS[k]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0" data-testid="dossier-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FolderPlus className="h-5 w-5 text-[#C9A227]" /> Nieuw dossier</DialogTitle>
          <DialogDescription>Stel een genummerd dossier samen met {noPay ? "" : "betalingsoverzicht, "}tijdslijn en bijlagen.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 text-xs mb-1 flex-wrap">
          {hidden.loaded && steps.map((s, i) => (
            <span key={s} className={`px-2 py-1 rounded-full ${i === step ? "bg-[#0F1E3D] text-white" : i < step ? "bg-[#C9A227]/20 text-[#0F1E3D]" : "bg-slate-100 text-slate-500"}`} data-testid={`dossier-step-${i}`}>{i + 1}. {s}</span>
          ))}
        </div>

        {cur === "gegevens" && (
          <div className="space-y-3">
            <div><Label>Titel *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Juridisch dossier na aanmaning" data-testid="dossier-title" /></div>
            <div><Label>Toelichting (optioneel)</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Korte omschrijving voor op het voorblad" data-testid="dossier-description" /></div>
          </div>
        )}

        {cur === "betalingen" && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={payInclude} onCheckedChange={() => setPayInclude((v) => !v)} data-testid="dossier-pay-include" /> Betalingsoverzicht opnemen</label>
            {payInclude && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Overeenkomst</Label>
                    <Combobox testId="dossier-sel" value={sel} onChange={setSel} searchPlaceholder="Zoek…" options={[{ value: "all", label: "Alle contracten" }, ...contracts.map((c) => ({ value: c.id, label: c.ovk_number || c.kind })), ...(noFact ? [] : [{ value: "factoring", label: "Factoring" }])]} />
                  </div>
                  <div className="flex flex-col justify-end gap-2 pb-1">
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={onlyOutstanding} onCheckedChange={() => { setOnlyOutstanding((v) => !v); setOnlyLate(false); }} data-testid="dossier-only-outstanding" /> Alleen openstaande termijnen</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={onlyLate} onCheckedChange={() => { setOnlyLate((v) => !v); setOnlyOutstanding(false); }} data-testid="dossier-only-late" /> Alleen te laat (achterstallig)</label>
                  </div>
                </div>
                <div className="border rounded-lg max-h-64 overflow-y-auto" data-testid="dossier-rows">
                  {loadingRows ? <p className="text-sm text-slate-400 p-3">Laden…</p> : visibleRows.length === 0 ? <p className="text-sm text-slate-400 p-3">Geen termijnen.</p> : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-500 border-b sticky top-0 bg-white"><th className="p-2"></th><th className="p-2">OVK</th><th className="p-2">Periode</th><th className="p-2">Vervaldatum</th><th className="p-2 text-right">Totaal</th><th className="p-2">Status</th></tr></thead>
                      <tbody>
                        {visibleRows.map((r) => (
                          <tr key={r.id} className="border-b last:border-0">
                            <td className="p-2"><Checkbox checked={!!checkedIds[r.id]} onCheckedChange={() => toggleRow(r.id)} data-testid={`dossier-row-${r.id}`} /></td>
                            <td className="p-2 text-xs">{r.ovk}</td>
                            <td className="p-2">{r.period}</td>
                            <td className="p-2">{fmtDate(r.due_date)}</td>
                            <td className="p-2 text-right">{fmtEur(r.due_total)}</td>
                            <td className="p-2"><Badge variant="outline" className="text-xs">{PSTAT[r.status] || r.status}{r.days_late > 0 ? ` (${r.days_late}d)` : ""}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <p className="text-xs text-slate-400">{visibleRows.filter((r) => checkedIds[r.id]).length} van {visibleRows.length} termijnen geselecteerd.</p>
              </>
            )}
          </div>
        )}

        {cur === "tijdslijn" && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={tlInclude} onCheckedChange={() => setTlInclude((v) => !v)} data-testid="dossier-tl-include" /> Communicatie-tijdslijn opnemen</label>
            {tlInclude && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Reikwijdte communicatie</Label>
                  <Combobox testId="dossier-tl-scope" value={tlScope} onChange={setTlScope} options={[{ value: "all", label: "Alle communicatie" }, ...(noPay ? [] : [{ value: "contract", label: "Alleen m.b.t. een bepaalde overeenkomst" }, { value: "late", label: "Alleen m.b.t. te laat betalingen" }])]} />
                </div>
                {tlScope === "contract" && (
                  <div>
                    <Label className="text-xs text-slate-500">Overeenkomst</Label>
                    <Combobox testId="dossier-tl-contract" value={tlContract} onChange={setTlContract} placeholder="Kies overeenkomst" searchPlaceholder="Zoek overeenkomst…" options={contracts.map((c) => ({ value: c.id, label: c.ovk_number || c.kind }))} />
                    <p className="text-xs text-slate-400 mt-1">Betalingen en gesprekken gekoppeld aan deze overeenkomst + alle brieven.</p>
                  </div>
                )}
                {tlScope === "late" && <p className="text-xs text-slate-400">Neemt te-laat/te-laat-betaalde termijnen, alle aanmaningen/herinneringen en gesprekken op.</p>}
                <Label className="text-xs text-slate-500">Onderdelen</Label>
                <div className="grid grid-cols-2 gap-2">
                  {kinds.map((k) => (
                    <label key={k.value} className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer">
                      <Checkbox checked={!!tlKinds[k.value]} onCheckedChange={() => setTlKinds((s) => ({ ...s, [k.value]: !s[k.value] }))} data-testid={`dossier-tlkind-${k.value}`} /> {k.label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">In de volgende stap kies je per bericht, brief of gesprek wat er in het dossier komt.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-slate-500">Vanaf</Label><DateField testId="dossier-from" value={from} onChange={setFrom} /></div>
                  <div><Label className="text-xs text-slate-500">Tot en met</Label><DateField testId="dossier-to" value={to} onChange={setTo} /></div>
                </div>
              </>
            )}
          </div>
        )}

        {cur === "selectie" && <DossierItemPicker items={tlItems} checked={tlChecked} setChecked={setTlChecked} />}

        {cur === "documenten" && (
          <div className="space-y-3" data-testid="dossier-step-documents">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">Kies welke documenten als bijlage in het dossier komen. PDF's worden achter het dossier gevoegd.</p>
              <div className="flex gap-2 text-xs shrink-0">
                <button className="underline text-[#0F1E3D]" onClick={() => setDocChecked(Object.fromEntries(docs.map((d) => [docKey(d), true])))} data-testid="dossier-docs-all">Alles</button>
                <button className="underline text-slate-500" onClick={() => setDocChecked({})} data-testid="dossier-docs-none">Niets</button>
              </div>
            </div>
            {docs.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">Geen documenten bij deze debiteur.</p> : (
              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                {docs.map((d) => (
                  <label key={docKey(d)} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`dossier-doc-${d.id}`}>
                    <Checkbox checked={!!docChecked[docKey(d)]} onCheckedChange={() => setDocChecked((s) => ({ ...s, [docKey(d)]: !s[docKey(d)] }))} />
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#C9A227]/15 text-[#0F1E3D] shrink-0">{d.category || DOC_SRC[d.source]}</span>
                    <span className="truncate flex-1">{d.title}</span>
                    <span className="text-xs text-slate-400 shrink-0">{d.date ? fmtDate(d.date) : ""}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500">{docsSelected.length} van {docs.length} geselecteerd</p>
          </div>
        )}

        {cur === "tekst" && (
          <div className="space-y-4" data-testid="dossier-step-text">
            <p className="text-xs text-slate-500">Dit is de voorgestelde tekst van het dossier. Pas aan wat je wilt. De PDF wordt met jouw tekst opgemaakt; de originele gegevens blijven ongewijzigd.</p>
            <div><Label className="text-xs text-slate-500">Toelichting op het voorblad</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="dossier-text-description" /></div>
            {clInclude && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Begeleidende brief</Label>
                <Input value={clSubject} onChange={(e) => setClSubject(e.target.value)} placeholder="Onderwerp" data-testid="dossier-text-cl-subject" />
                <Textarea value={clBody} onChange={(e) => setClBody(e.target.value)} rows={6} data-testid="dossier-text-cl-body" />
              </div>
            )}
            {tlInclude && <><Label className="text-xs text-slate-500">Tijdslijn ({tlSelected.length} items)</Label><DossierTextEditor items={tlSelected} texts={texts} setTexts={setTexts} /></>}
          </div>
        )}

        {cur === "brief" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={clInclude} onCheckedChange={() => setClInclude((v) => !v)} data-testid="dossier-cl-include" /> Begeleidende brief toevoegen</label>
              <Button type="button" size="sm" variant="outline" className="border-[#C9A227]/70 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={aiCover} disabled={aiBusy} data-testid="dossier-cl-ai"><Sparkles className="h-4 w-4 mr-1.5" /> {aiBusy ? "Bezig…" : "AI-suggestie"}</Button>
            </div>
            {clInclude && (
              <div className="space-y-2 border rounded-lg p-3">
                <div><Label className="text-xs text-slate-500">Onderwerp</Label><Input value={clSubject} onChange={(e) => setClSubject(e.target.value)} data-testid="dossier-cl-subject" /></div>
                <div><Label className="text-xs text-slate-500">Inhoud</Label><Textarea value={clBody} onChange={(e) => setClBody(e.target.value)} rows={8} data-testid="dossier-cl-body" /></div>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Extra PDF-bijlagen</Label>
              <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed rounded-lg py-4 cursor-pointer hover:bg-slate-50 text-sm text-slate-500" data-testid="dossier-upload-label">
                <Upload className="h-4 w-4" /> {uploading ? "Uploaden…" : "Klik om PDF's te uploaden"}
                <input type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files || []))} data-testid="dossier-upload-input" />
              </label>
              <div className="space-y-1 mt-2">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border rounded-lg px-3 py-1.5" data-testid={`dossier-attachment-${i}`}>
                    <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-red-500" /> {a.name}</span>
                    <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {cur === "overzicht" && (
          <div className="space-y-2 text-sm" data-testid="dossier-summary">
            <p><strong>Titel:</strong> {title || "—"}<EditLink to="gegevens" /></p>
            {description && <p><strong>Toelichting:</strong> {description}</p>}
            {!noPay && <p><strong>Betalingsoverzicht:</strong> {payInclude ? `${visibleRows.filter((r) => checkedIds[r.id]).length} termijn(en) — ${sel === "all" ? "Alle contracten" : sel === "factoring" ? "Factoring" : (contracts.find((c) => c.id === sel)?.ovk_number || "Contract")}${onlyLate ? " (alleen te laat)" : onlyOutstanding ? " (alleen openstaand)" : ""}` : "niet opnemen"}<EditLink to="betalingen" /></p>}
            <p><strong>Tijdslijn:</strong> {tlInclude ? `${kinds.filter((k) => tlKinds[k.value]).length} onderdeel(en) · ${tlScope === "contract" ? "per overeenkomst" : tlScope === "late" ? "te laat betalingen" : "alle communicatie"}${from || to ? ` · ${from ? fmtDate(from) : "…"} t/m ${to ? fmtDate(to) : "…"}` : ""}` : "niet opnemen"}<EditLink to="tijdslijn" /></p>
            {tlInclude && <p><strong>Selectie:</strong> {tlSelected.length} van {tlItems.length} items · {Object.keys(texts).length} tekst(en) aangepast<EditLink to="selectie" /></p>}
            <p><strong>Begeleidende brief:</strong> {clInclude ? (clSubject || "toegevoegd") : "geen"}<EditLink to="brief" /></p>
            <p><strong>Documenten:</strong> {docsSelected.length} van {docs.length}<EditLink to="documenten" /></p>
            <p><strong>Bijlagen:</strong> {attachments.length} PDF('s)<EditLink to="brief" /></p>
            <p className="text-xs text-slate-400 pt-2">Er wordt automatisch een dossiernummer (DOS-{new Date().getFullYear()}-…) toegekend en een samengesteld PDF-dossier bewaard.</p>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="dossier-prev"><ChevronLeft className="h-4 w-4 mr-1" /> Vorige</Button>
          {step < steps.length - 1
            ? <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setStep((s) => s + 1)} data-testid="dossier-next">Volgende <ChevronRight className="h-4 w-4 ml-1" /></Button>
            : <Button className="bg-[#C9A227] hover:bg-[#B8921F] text-[#0F1E3D] font-semibold" onClick={create} disabled={busy} data-testid="dossier-create"><FolderPlus className="h-4 w-4 mr-2" /> {busy ? "Bezig…" : "Dossier aanmaken"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
